/**
 * Account Settings Page
 * Allows account owners to manage account settings, view subscription details, and disable account
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetAccount, useUpdateAccount, useDisableAccount, useTransferOwnership } from '../../hooks/useAccount';
import { useGetMembers } from '../../hooks/useTeam';
import { useAuthStore } from '../../store/authStore';
import { format } from 'date-fns';
import type { UpdateAccountData, DisableAccountData, TransferOwnershipData } from '../../types';

// Reusable components
const Card = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <div className={`bg-white shadow rounded-lg ${className}`}>{children}</div>
);

const CardHeader = ({ children }: { children: React.ReactNode }) => (
  <div className="px-6 py-4 border-b border-gray-200">{children}</div>
);

const CardTitle = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-lg font-semibold text-gray-900">{children}</h2>
);

const CardContent = ({ children }: { children: React.ReactNode }) => (
  <div className="px-6 py-4">{children}</div>
);

const Badge = ({ children, variant = 'default' }: { children: React.ReactNode; variant?: 'default' | 'success' | 'warning' | 'danger' }) => {
  const variants = {
    default: 'bg-gray-100 text-gray-800',
    success: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    danger: 'bg-red-100 text-red-800',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variants[variant]}`}>
      {children}
    </span>
  );
};

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full z-10">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">{title}</h3>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function AccountSettingsPage() {
  const { t } = useTranslation('account_settings');
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { data: account, isLoading } = useGetAccount();
  const { data: members } = useGetMembers();
  const updateAccount = useUpdateAccount();
  const disableAccount = useDisableAccount();
  const transferOwnership = useTransferOwnership();

  const [isEditingName, setIsEditingName] = useState(false);
  const [accountName, setAccountName] = useState('');
  const [disableModalOpen, setDisableModalOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [confirmDisable, setConfirmDisable] = useState(false);

  // Transfer ownership state
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState<number | ''>('');
  const [transferPassword, setTransferPassword] = useState('');

  // Check if current user is the account owner
  const isOwner = user?.id === account?.owner.id;

  // Check if user has paid plan
  const hasPaidPlan = user?.account?.can_use_platform ?? false;

  // Filter out current owner from members list
  const availableMembers = members?.filter(member => member.id !== user?.id) || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">{t('loading')}</div>
      </div>
    );
  }

  if (!account) {
    // Check if user is a system admin without an account
    if (user?.role === 'admin' && !user?.account_id) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="max-w-md text-center">
            <div className="mb-4">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">System Administrator Account</h3>
            <p className="text-sm text-gray-500 mb-4">
              System administrators don't have account settings. This page is only available for regular account members.
            </p>
            <p className="text-sm text-gray-500">
              Use the admin panel to manage user accounts and system settings.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500">{t('error_loading')}</div>
      </div>
    );
  }

  const handleStartEdit = () => {
    setAccountName(account.name);
    setIsEditingName(true);
  };

  const handleCancelEdit = () => {
    setAccountName('');
    setIsEditingName(false);
  };

  const handleSaveAccountName = (e: React.FormEvent) => {
    e.preventDefault();
    if (!accountName.trim()) return;

    const data: UpdateAccountData = { name: accountName.trim() };
    updateAccount.mutate(data, {
      onSuccess: () => {
        setIsEditingName(false);
        setAccountName('');
      },
    });
  };

  const handleDisableAccount = (e: React.FormEvent) => {
    e.preventDefault();
    if (!disablePassword.trim()) return;

    const data: DisableAccountData = { password: disablePassword };
    disableAccount.mutate(data, {
      onSuccess: () => {
        setDisableModalOpen(false);
        setDisablePassword('');
        // Redirect to login after account is disabled
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      },
    });
  };

  const handleTransferOwnership = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMemberId || !transferPassword.trim()) return;

    const data: TransferOwnershipData = {
      new_owner_id: selectedMemberId as number,
      password: transferPassword.trim(),
    };

    transferOwnership.mutate(data, {
      onSuccess: () => {
        setTransferModalOpen(false);
        setSelectedMemberId('');
        setTransferPassword('');
        // Optionally redirect to dashboard since user is no longer owner
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      },
    });
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{t('page_title')}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {t('page_subtitle')}
        </p>
      </div>

      <div className="space-y-6">
        {/* Account Information */}
        <Card>
          <CardHeader>
            <CardTitle>{t('account_information.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Account Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('account_information.name_label')}
                </label>
                {isEditingName ? (
                  <form onSubmit={handleSaveAccountName} className="flex gap-2">
                    <input
                      type="text"
                      value={accountName}
                      onChange={(e) => setAccountName(e.target.value)}
                      className="flex-1 input"
                      placeholder={t('account_information.name_placeholder')}
                      autoFocus
                    />
                    <button
                      type="submit"
                      disabled={updateAccount.isPending || !accountName.trim()}
                      className="btn btn-primary"
                    >
                      {updateAccount.isPending ? t('account_information.saving') : t('account_information.save_button')}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      disabled={updateAccount.isPending}
                      className="btn btn-secondary"
                    >
                      {t('account_information.cancel_button')}
                    </button>
                  </form>
                ) : (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-900">{account.name}</span>
                    {isOwner && (
                      <button
                        onClick={handleStartEdit}
                        className="text-sm text-indigo-600 hover:text-indigo-500"
                      >
                        {t('account_information.edit_button')}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Account Owner */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('account_information.owner_label')}
                </label>
                <div className="text-gray-900">{account.owner.name}</div>
                <div className="text-sm text-gray-500">{account.owner.email}</div>
              </div>

              {/* Account Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('account_information.status_label')}
                </label>
                <Badge variant={account.status === 'active' ? 'success' : 'danger'}>
                  {account.status === 'active' ? t('account_information.status_active') : t('account_information.status_disabled')}
                </Badge>
              </div>

              {/* Created Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('account_information.created_label')}
                </label>
                <div className="text-gray-900">
                  {format(new Date(account.created_at), 'MMMM d, yyyy')}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subscription Details - Only show if user has paid */}
        {hasPaidPlan && account.subscription.plan && (
          <Card>
            <CardHeader>
              <CardTitle>{t('subscription.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              {account.subscription.plan ? (
              <div className="space-y-4">
                {/* Plan Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('subscription.current_plan_label')}
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-900 font-medium">
                      {account.subscription.plan.name}
                    </span>
                    <Badge variant={account.subscription.is_active ? 'success' : 'warning'}>
                      {account.subscription.is_active ? t('subscription.status_active') : t('subscription.status_inactive')}
                    </Badge>
                  </div>
                </div>

                {/* Pricing */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('subscription.price_label')}
                  </label>
                  <div className="text-gray-900">
                    ${account.subscription.plan.price} /{' '}
                    {account.subscription.plan.billing_period === 'monthly' ? t('subscription.price_monthly') : t('subscription.price_yearly')}
                  </div>
                </div>

                {/* Subscription Period */}
                {account.subscription.starts_at && account.subscription.ends_at && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('subscription.period_label')}
                    </label>
                    <div className="text-gray-900">
                      {format(new Date(account.subscription.starts_at), 'MMM d, yyyy')} -{' '}
                      {format(new Date(account.subscription.ends_at), 'MMM d, yyyy')}
                    </div>
                  </div>
                )}
              </div>
              ) : (
                <div className="text-gray-500">{t('subscription.no_subscription')}</div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Usage Metrics - Only show if user has paid */}
        {hasPaidPlan && (
          <Card>
            <CardHeader>
              <CardTitle>{t('usage_metrics.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('usage_metrics.total_campaigns')}
                  </label>
                  <div className="text-2xl font-semibold text-gray-900">
                    {account.statistics.total_campaigns}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('usage_metrics.total_requests')}
                  </label>
                  <div className="text-2xl font-semibold text-gray-900">
                    {account.statistics.total_requests.toLocaleString()}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('usage_metrics.team_members')}
                  </label>
                  <div className="text-2xl font-semibold text-gray-900">
                    {account.statistics.team_members}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Transfer Ownership - Only visible to account owner */}
        {isOwner && availableMembers.length > 0 && (
          <Card className="border-2 border-yellow-200">
            <CardHeader>
              <CardTitle>{t('transfer_ownership.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-1">
                    {t('transfer_ownership.section_title')}
                  </h3>
                  <p className="text-sm text-gray-500 mb-3">
                    {t('transfer_ownership.description')}
                  </p>
                  <button
                    onClick={() => setTransferModalOpen(true)}
                    className="btn btn-secondary"
                  >
                    {t('transfer_ownership.button')}
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Danger Zone - Only visible to account owner */}
        {isOwner && (
          <Card className="border-2 border-red-200">
            <CardHeader>
              <CardTitle>{t('danger_zone.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-1">
                    {t('danger_zone.disable_title')}
                  </h3>
                  <p className="text-sm text-gray-500 mb-3">
                    {t('danger_zone.disable_description')}
                  </p>
                  <button
                    onClick={() => setDisableModalOpen(true)}
                    className="btn btn-danger"
                  >
                    {t('danger_zone.disable_button')}
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Disable Account Modal */}
      <Modal
        isOpen={disableModalOpen}
        onClose={() => {
          setDisableModalOpen(false);
          setDisablePassword('');
          setConfirmDisable(false);
        }}
        title={t('danger_zone.modal_title')}
      >
        <form onSubmit={handleDisableAccount}>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-red-900 mb-2">
                {t('danger_zone.warning_title')}
              </h4>
              <p className="text-sm text-red-700 mb-3">
                {t('danger_zone.warning_description')}
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm text-red-700">
                <li>{t('danger_zone.warning_item_1', { count: account?.statistics.team_members || 0, plural: (account?.statistics.team_members || 0) !== 1 ? 's' : '' })}</li>
                <li>{t('danger_zone.warning_item_2', { count: account?.statistics.total_campaigns || 0, plural: (account?.statistics.total_campaigns || 0) !== 1 ? 's' : '' })}</li>
                <li>{t('danger_zone.warning_item_3')}</li>
                <li>{t('danger_zone.warning_item_4')}</li>
                <li>{t('danger_zone.warning_item_5')}</li>
              </ul>
            </div>

            <div>
              <label htmlFor="disable-password" className="label">
                {t('danger_zone.password_label')}
              </label>
              <input
                id="disable-password"
                type="password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                className="input"
                placeholder={t('danger_zone.password_placeholder')}
                required
                autoFocus
              />
            </div>

            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="confirm-disable"
                  type="checkbox"
                  checked={confirmDisable}
                  onChange={(e) => setConfirmDisable(e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  required
                />
              </div>
              <div className="ml-3">
                <label htmlFor="confirm-disable" className="text-sm text-gray-700">
                  {t('danger_zone.checkbox_label')}
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-5">
              <button
                type="button"
                onClick={() => {
                  setDisableModalOpen(false);
                  setDisablePassword('');
                }}
                disabled={disableAccount.isPending}
                className="btn btn-secondary"
              >
                {t('danger_zone.cancel_button')}
              </button>
              <button
                type="submit"
                disabled={disableAccount.isPending || !disablePassword.trim() || !confirmDisable}
                className="btn btn-danger"
              >
                {disableAccount.isPending ? t('danger_zone.submitting') : t('danger_zone.submit_button')}
              </button>
            </div>
          </div>
        </form>
      </Modal>

      {/* Transfer Ownership Modal */}
      <Modal
        isOpen={transferModalOpen}
        onClose={() => {
          setTransferModalOpen(false);
          setSelectedMemberId('');
          setTransferPassword('');
        }}
        title={t('transfer_ownership.modal_title')}
      >
        <form onSubmit={handleTransferOwnership}>
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-yellow-900 mb-2">
                {t('transfer_ownership.warning_title')}
              </h4>
              <p className="text-sm text-yellow-700 mb-3">
                {t('transfer_ownership.warning_description')}
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm text-yellow-700">
                <li>{t('transfer_ownership.warning_item_1')}</li>
                <li>{t('transfer_ownership.warning_item_2')}</li>
                <li>{t('transfer_ownership.warning_item_3')}</li>
                <li>{t('transfer_ownership.warning_item_4')}</li>
                <li>{t('transfer_ownership.warning_item_5')}</li>
              </ul>
              <p className="text-sm text-yellow-700 mt-3">
                {t('transfer_ownership.warning_note')}
              </p>
            </div>

            <div>
              <label htmlFor="transfer-member" className="label">
                {t('transfer_ownership.select_label')}
              </label>
              <select
                id="transfer-member"
                value={selectedMemberId}
                onChange={(e) => setSelectedMemberId(e.target.value === '' ? '' : Number(e.target.value))}
                className="input"
                required
                autoFocus
              >
                <option value="">{t('transfer_ownership.select_placeholder')}</option>
                {availableMembers.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name} ({member.email})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="transfer-password" className="label">
                {t('transfer_ownership.password_label')}
              </label>
              <input
                id="transfer-password"
                type="password"
                value={transferPassword}
                onChange={(e) => setTransferPassword(e.target.value)}
                className="input"
                placeholder={t('transfer_ownership.password_placeholder')}
                required
              />
            </div>

            <div className="flex justify-end gap-3 mt-5">
              <button
                type="button"
                onClick={() => {
                  setTransferModalOpen(false);
                  setSelectedMemberId('');
                  setTransferPassword('');
                }}
                disabled={transferOwnership.isPending}
                className="btn btn-secondary"
              >
                {t('transfer_ownership.cancel_button')}
              </button>
              <button
                type="submit"
                disabled={transferOwnership.isPending || !selectedMemberId || !transferPassword.trim()}
                className="btn btn-primary"
              >
                {transferOwnership.isPending ? t('transfer_ownership.submitting') : t('transfer_ownership.submit_button')}
              </button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
}
